import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAppSelector } from '@/store'
import Button from '@/components/Button'
import { HiArrowLeft, HiHome } from 'react-icons/hi'

interface ErrorPageProps {
    error?: Error
    statusCode?: number
    message?: string
}

const ErrorPage = ({ error, statusCode, message }: ErrorPageProps) => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const userAuth = useAppSelector((state) => state.auth.user)
    const isAuthenticated = !!userAuth?.authority?.length

    // Get error details from URL params or props
    const finalStatusCode =
        statusCode || parseInt(searchParams.get('statusCode') || '404')
    const finalMessage =
        message || searchParams.get('message') || 'Page not found'

    const handleBackToLogin = (e: React.MouseEvent) => {
        e.preventDefault()
        navigate('/sign-in', { replace: true })
    }

    const handleGoToDashboard = (e: React.MouseEvent) => {
        e.preventDefault()

        // Try to navigate to /home first
        try {
            navigate('/home', { replace: true })
        } catch (error) {
            // Fallback to root
            navigate('/', { replace: true })
        }
    }

    const handleRequestPasswordReset = (e: React.MouseEvent) => {
        e.preventDefault()
        navigate('/forgot-password', { replace: true })
    }

    // Check if this is a token-related error
    const isTokenError =
        finalMessage.toLowerCase().includes('token') ||
        finalMessage.toLowerCase().includes('reset') ||
        finalStatusCode === 400

    return (
        <div className="h-screen w-full flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-900">
            <div className="text-center max-w-md w-full">
                <h1 className="text-9xl font-bold text-red-500 dark:text-red-400 mb-4">
                    {finalStatusCode}
                </h1>
                <h2 className="text-3xl font-semibold mb-4 text-gray-900 dark:text-white">
                    {finalStatusCode === 404
                        ? 'Page Not Found'
                        : 'Oops! Something went wrong'}
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-8 text-lg">
                    {finalMessage ||
                        error?.message ||
                        'The page you are looking for might have been removed or is temporarily unavailable.'}
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                    {isTokenError && (
                        <Button
                            className="w-full sm:w-auto justify-center"
                            variant="solid"
                            onClick={handleRequestPasswordReset}
                        >
                            Request New Reset Link
                        </Button>
                    )}
                    {isAuthenticated ? (
                        <Button
                            className="w-full sm:w-auto justify-center"
                            icon={<HiHome />}
                            variant={isTokenError ? 'default' : 'solid'}
                            onClick={handleGoToDashboard}
                        >
                            Go to Dashboard
                        </Button>
                    ) : (
                        <Button
                            className="w-full sm:w-auto justify-center"
                            icon={<HiArrowLeft />}
                            variant={isTokenError ? 'default' : 'solid'}
                            onClick={handleBackToLogin}
                        >
                            Back to Login
                        </Button>
                    )}
                    {!isTokenError && (
                        <Button
                            className="w-full sm:w-auto justify-center"
                            variant="plain"
                            onClick={() => window.location.reload()}
                        >
                            Try Again
                        </Button>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ErrorPage
